import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { newsAPI } from '../services/api';
import {
  Newspaper,
  Clock,
  ExternalLink,
  Loader2,
  ArrowLeft,
  RefreshCw
} from 'lucide-react';

const Actualities: React.FC = () => {
  const [actualities, setActualities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActualities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await newsAPI.getAgricultureNews({ limit: 20 });
      if (response.data.success) {
        setActualities(response.data.data || []);
      } else {
        setError('Failed to load news');
      }
    } catch (err: any) {
      console.error('Error fetching actualities:', err);
      setError(err.response?.data?.message || 'Failed to load news');
      setActualities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActualities();
  }, []);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading news...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Link
                to="/"
                className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-4 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Link>
              <h1 className="text-4xl font-extrabold bg-gradient-to-r from-indigo-600 to-purple-500 bg-clip-text text-transparent mb-2">
                Agriculture News
              </h1>
              <p className="text-gray-600">
                Latest news, discussions, and updates from the agricultural community
              </p>
            </div>
            <button
              onClick={fetchActualities}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Actualities Grid */}
        {actualities.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-gray-300">
            <Newspaper className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg text-gray-500">No agriculture news available at the moment</p>
            <button
              onClick={fetchActualities}
              className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {actualities.map((actuality, index) => {
              const textContent = actuality.description?.replace(/<[^>]*>/g, '').trim() || '';
              const preview = textContent.length > 150 ? textContent.substring(0, 150) + '...' : textContent;
              const sourceName = actuality.source || 'News Source';
              const sourceInitial = sourceName.charAt(0).toUpperCase();

              const articleUrl = actuality.url && actuality.url.startsWith('http')
                ? actuality.url
                : actuality.url
                  ? `https://${actuality.url}`
                  : '#';

              return (
                <a
                  key={index}
                  href={articleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (!actuality.url || articleUrl === '#') {
                      e.preventDefault();
                      return;
                    }
                  }}
                  className="block bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                >
                  {/* Image */}
                  {actuality.image && (
                    <div className="relative h-48 overflow-hidden bg-gray-200">
                      <img
                        src={actuality.image}
                        alt={actuality.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  <div className="p-6">
                    {/* Source and Date */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white text-xs font-bold flex items-center justify-center">
                          {sourceInitial}
                        </div>
                        <span className="text-sm font-medium text-gray-700">{sourceName}</span>
                      </div>
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>{formatDate(actuality.publishedAt)}</span>
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-indigo-600 transition-colors">
                      {actuality.title}
                    </h3>

                    {/* Description */}
                    {preview && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                        {preview}
                      </p>
                    )}

                    {/* Author */}
                    {actuality.author && (
                      <p className="text-xs text-gray-500 mb-4">
                        By {actuality.author}
                      </p>
                    )}

                    {/* Read More Link */}
                    <div className="inline-flex items-center text-indigo-600 group-hover:text-indigo-700 font-medium text-sm transition-colors">
                      Read More
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}

        {/* View More Link */}
        {actualities.length > 0 && (
          <div className="mt-8 text-center">
            <a
              href="https://news.google.com/search?q=agriculture+farming"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
            >
              <Newspaper className="h-5 w-5 mr-2" />
              View More News on Google News
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Actualities;
